import { useRef } from 'react';
import { useLazyLoadQuery, graphql } from 'react-relay';
import { loadStripe, Stripe } from '@stripe/stripe-js';

import { useStripePromiseQuery, Currency } from './__generated__/useStripePromiseQuery.graphql';

const stripeClientTokenQuery = graphql`
    query useStripePromiseQuery($currency: Currency!) {
        viewer {
            stripeClientToken(currency: $currency) {
                value
            }
        }
    }
`;

// we need to make sure the value we pass to the stripe element does not change
// this hook returns the same promise object for each re-render, and resolves it once both the stripe token query and stripe loading completes
export function useStripePromise(currency: Currency): Promise<Stripe> {
    const hasResolved = useRef<boolean>(false);
    const stripePromise = useRef<Promise<Stripe> | null>(null);
    const stripePromiseResolve = useRef<(stripe: Stripe) => void>(() => {});

    if (!stripePromise.current) {
        stripePromise.current = new Promise<Stripe>(resolve => {
            stripePromiseResolve.current = resolve;
        });
    }

    const {
        viewer: { stripeClientToken },
    } = useLazyLoadQuery<useStripePromiseQuery>(stripeClientTokenQuery, { currency });

    if (!hasResolved.current && stripeClientToken?.value) {
        loadStripe(stripeClientToken.value).then(stripe => {
            if (stripe) {
                stripePromiseResolve.current(stripe);

                hasResolved.current = true;
            }
        });
    }

    return stripePromise.current;
}
